import { RouteEnums } from '@Helper/Enums';
import MenuIcon from '@mui/icons-material/Menu';
import {
  CSSObject,
  Divider,
  IconButton,
  List,
  ListItemText,
  Menu,
  MenuItem,
  styled,
  Theme,
} from '@mui/material';
import MuiDrawer from '@mui/material/Drawer';
import { postDashboardViewWidget } from '@Store/feature/dashboardSlice';
import {
  getUserDealspaceList,
  postUserDealspaceList,
} from '@Store/feature/dealspaceSlice';
import {
  postPricingTrialEndDate,
  postPricingType,
} from '@Store/feature/pricingSlice';
import { postPurePlaybookList } from '@Store/feature/purePlaybookSlice';
import moment from 'moment';
import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { Icons } from '../../../assets/SvgIcons';
import { Dealspace } from '../../../config/dealspace';
import {
  ComponentHelper,
  useEffectAsync,
  UserHelper,
} from '../../../helper/Infra';
import {
  ICTask,
  ICUserDealspace,
  SubscriptionType,
} from '../../../helper/Interfaces';
import { LocalStorageHelper } from '../../../helper/LocalStorageHelper';
import {
  getAssetsCurrentSelectedDealspace,
  getAssetsDealspaces,
  getAssetsFunctionalAreas,
  getAssetsPlaybooks,
  postCurrentSelectedDealspace,
} from '../../../store/feature/assetsSlice';
import {
  getdirectoryDetail,
  getIsUserIMO,
  postDirectoryList,
} from '../../../store/feature/directory';
import {
  getPlayBookDetail,
  postDealTaskList,
} from '../../../store/feature/playBookDetailSlice';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import NavPlaybook from '../../UI/NavPlaybook/NavPlaybook';
import CustomListItem from './CustomListItem';
import classes from './Navigation.module.css';

interface IProps {
  menuShow?: boolean;
  toggleMenu?: () => void;
}

const drawerWidth = 240;

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up('sm')]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
}));

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: prop => prop !== 'open',
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  ...(open && {
    ...openedMixin(theme),
    '& .MuiDrawer-paper': openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    '& .MuiDrawer-paper': closedMixin(theme),
  }),
}));

const Navigation: FC<IProps> = ({ menuShow, toggleMenu }) => {
  const { workSpaceId, dealTaskList } = useAppSelector(getPlayBookDetail);
  let navigate = useNavigate();
  const { id, pid } = useParams();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const playbooks = useAppSelector(getAssetsPlaybooks);
  const dealspaces = useAppSelector(getAssetsDealspaces);
  const dealspaceList = useAppSelector(getUserDealspaceList);
  const directoryList = useAppSelector(getdirectoryDetail);
  const currentSelectedDealspace = useAppSelector(
    getAssetsCurrentSelectedDealspace
  );

  const [dealspaceCollection, setDealspaceCollection] = useState<Dealspace[]>(
    []
  );

  const [dealspacesWithNotification, setDealspacesWithNotification] = useState<
    ICUserDealspace[]
  >([]);

  const [notificationCount, setNotificationCount] = useState<number>(0);
  const [currentDealTaskList, setCurrentDealTaskList] = useState<ICTask[]>([]);
  const [selectedDealspace, setSelectedDealspace] = useState<
    Dealspace | undefined
  >(currentSelectedDealspace);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [refresh] = useState(false);
  const open = Boolean(anchorEl);
  const componentHelper = new ComponentHelper();
  const userHelper = new UserHelper();

  const isUserImo = useAppSelector(getIsUserIMO);
  const openMoreMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const closeMoreMenu = () => {
    setAnchorEl(null);
  };

  useEffectAsync(async () => {
    if (!dealspaceList) {
      const userDealspaces =
        (await userHelper.getUserDealspaces()) as ICUserDealspace[];
      console.log('userDealspaces', userDealspaces);
      const subscriptionType = userDealspaces.find(i => i.id === (id ?? ''))
        ?.subscriptionType
        ? (userDealspaces.find(i => i.id === (id ?? ''))
            ?.subscriptionType as SubscriptionType)
        : SubscriptionType.TRIAL;
      dispatch(postPricingType(subscriptionType));
      dispatch(
        postPricingTrialEndDate(
          userDealspaces.find(i => i.id === (id ?? ''))?.trialEndDate ?? ''
        )
      );
      setDealspacesWithNotification(userDealspaces);
      dispatch(postUserDealspaceList(userDealspaces));
    } else {
      const subscriptionType = dealspaceList.find(i => i.id === (id ?? ''))
        ?.subscriptionType
        ? (dealspaceList.find(i => i.id === (id ?? ''))
            ?.subscriptionType as SubscriptionType)
        : SubscriptionType.TRIAL;
      dispatch(postPricingType(subscriptionType));
      dispatch(
        postPricingTrialEndDate(
          dealspaceList.find(i => i.id === (id ?? ''))?.trialEndDate ?? ''
        )
      );
      setDealspacesWithNotification(dealspaceList);
    }
    setDealspaceCollection(dealspaces ?? []);
    if (!currentSelectedDealspace || currentSelectedDealspace.id !== id) {
      if (dealspaces && dealspaces.length > 0 && pid)
        dispatch(
          postCurrentSelectedDealspace(
            dealspaces?.find(
              i =>
                i.id === (playbooks?.find(i => i.id === pid)?.dealspaceId ?? '')
            ) || undefined
          )
        );
      if (dealspaces && !pid && id) {
        dispatch(
          postCurrentSelectedDealspace(
            dealspaces?.length ? dealspaces?.find(i => i.id === id) : undefined
          )
        );
      }
    } else {
      setSelectedDealspace(currentSelectedDealspace);
    }
  }, [dealspaces, id, pid, playbooks, currentSelectedDealspace]);

  useEffectAsync(async () => {
    if (id || pid) {
      const dealspaceTaskListResponse = await componentHelper.getDealspaceTasks(
        id ?? playbooks?.find(p => p.id === pid!)?.dealspaceId ?? ''
      );

      setCurrentDealTaskList(dealspaceTaskListResponse);
      dispatch(postDealTaskList(dealspaceTaskListResponse));
      const notificationCountResponse =
        await componentHelper.unReadNotificationCount(
          id ?? playbooks?.find(p => p.id === pid!)?.dealspaceId ?? ''
        );
      setNotificationCount(notificationCountResponse?.unreadCount ?? 0);
    }
  }, [id, pid]);

  useEffect(() => {
    setCurrentDealTaskList(dealTaskList);
  }, [dealTaskList]);

  useEffectAsync(async () => {
    if ((directoryList === undefined || directoryList.length === 0) && id) {
      const dealspaceDirectoryResponse =
        await componentHelper.getDealspaceDirectory(id);
      dispatch(postDirectoryList(dealspaceDirectoryResponse));
    }
  }, [id]);

  const localStorageHelper = new LocalStorageHelper();

  const [openDrawer, setOpenDrawer] = React.useState(true);

  const handleDrawerOpen = () => {
    setOpenDrawer(!openDrawer);
  };

  return (
    <Drawer
      variant="permanent"
      open={openDrawer}
      className={openDrawer ? 'openDrawer' : 'closeDrawer'}
    >
      <DrawerHeader className={classes.drawerHeader}>
        <IconButton onClick={handleDrawerOpen}>
          <MenuIcon />
        </IconButton>
        <ListItemText
          sx={{
            opacity: openDrawer ? 1 : 0,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
          title={selectedDealspace?.name}
        >
          {selectedDealspace?.name}
        </ListItemText>
        {openDrawer ? (
          <IconButton onClick={openMoreMenu}>
            <Icons.Unfold />
          </IconButton>
        ) : null}
        <Menu
          id="basic-menu"
          anchorEl={anchorEl}
          open={open}
          onClose={closeMoreMenu}
          MenuListProps={{
            'aria-labelledby': 'basic-button',
          }}
          PaperProps={{
            style: {
              background: '#ffffff',
              border: ' 1px solid #ebebf0',
              boxSizing: 'border-box',
              boxShadow: ' 0px 8px 16px rgba(0, 0, 0, 0.1)',
              borderRadius: 4,
              padding: '8px 8px 6px',
              width: 280,
            },
          }}
        >
          <div className={classes.infoHeader}>
            <div
              style={{
                marginLeft: 10,
                marginTop: 10,
              }}
            >
              <div
                style={{
                  color: '#383463',
                  fontSize: 20,
                  fontWeight: 600,
                  paddingBottom: 10,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
                title={selectedDealspace?.name}
              >
                {selectedDealspace?.name}
              </div>
              {dealspacesWithNotification?.length > 0 &&
                dealspacesWithNotification
                  ?.filter(d => d.id === selectedDealspace?.id!)
                  ?.map((d, index) => {
                    return (
                      <div key={index}>
                        <div className={classes.infoBody}>
                          {d.buyerCompany} - {d.sellerCompany}
                        </div>
                        <div className={classes.infoBody}>
                          Day 1:{' '}
                          {d.dayOneDate
                            ? moment(d.dayOneDate.split('T')[0]).format(
                                'MM/DD/YYYY'
                              )
                            : ''}
                        </div>
                        <div className={classes.infoBody}>
                          Admin: {d.dealspaceOwner ?? ''}
                        </div>
                      </div>
                    );
                  })}
            </div>
          </div>
          <div className={classes.switchDealspace}>Switch Dealspace</div>

          {dealspacesWithNotification.map(dealspace => {
            return (
              <MenuItem
                key={dealspace.id}
                className={classes.dealspaceItem}
                onClick={() => {
                  dispatch(
                    postCurrentSelectedDealspace(
                      dealspaceCollection.find(d => d.id === dealspace.id)
                    )
                  );
                  localStorageHelper.setTaskTray([]);
                  //dispatch(postPurePlaybookList(undefined));
                  dispatch(postDashboardViewWidget([]));

                  const dealspaceTaskCount =
                    dealspacesWithNotification?.find(
                      d => d.id === dealspace?.id
                    )?.taskCount ?? 0;
                  if (dealspaceTaskCount > 0) {
                    navigate(RouteEnums.Dashboard + dealspace.id);
                  } else {
                    navigate(
                      playbooks?.find(i => i.dealspaceId === dealspace.id)
                        ? `${RouteEnums.Playbook}${dealspace.id}/${
                            playbooks?.find(i => i.dealspaceId === dealspace.id)
                              ?.id
                          }/2`
                        : RouteEnums.Dashboard + dealspace.id
                    );
                  }
                  dispatch(postDirectoryList(undefined));
                  closeMoreMenu();
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    width: '100%',
                    justifyContent: 'space-between',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      width: '100%',
                      justifyContent: 'space-between',
                    }}
                  >
                    <div
                      style={{
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      <div
                        className={classes.dealspaceName}
                        title={dealspace.name}
                      >
                        {dealspace.name}
                      </div>
                      <div
                        className={classes.companyName}
                        title={dealspace.name}
                      >
                        {dealspace.buyerCompany ?? ''} -{' '}
                        {dealspace.sellerCompany ?? ''}
                      </div>
                    </div>
                    <div className={classes.taskNumber}>
                      <div className={classes.taskCountWhite}>
                        {dealspace.notificationCount}
                      </div>
                    </div>
                    <div className={classes.IconArrow}>
                      <Icons.RightArrow />
                    </div>
                  </div>
                </div>
              </MenuItem>
            );
          })}
          <div
            style={{
              width: '100%',
              height: 0,
              border: '1px solid #dfe1e6',
              marginTop: 4,
            }}
          ></div>
          <MenuItem
            style={{ paddingLeft: 0 }}
            onClick={() => {
              navigate(RouteEnums.CreateDealspace);
            }}
          >
            <div
              className="MenuItemRow"
              style={{
                display: 'flex',
                justifyContent: 'space-evenly',
                alignItems: 'center',
              }}
            >
              <div>
                <Icons.Add />
              </div>
              <div style={{ marginLeft: 5 }}>Add new dealspace</div>
            </div>
          </MenuItem>
        </Menu>
      </DrawerHeader>
      <Divider style={{ border: '1px solid #dfe1e6' }} />
      <div className={[classes.NavigationBody, classes.customScroll].join(' ')}>
        <List>
          <CustomListItem
            link={`${RouteEnums.Dashboard}${
              selectedDealspace?.id ?? workSpaceId ?? id
            }`}
            name={t('navigation.dashboard')}
            icon={<Icons.DashboardNav />}
            openDrawer={openDrawer}
            id={'dashBoardNavigation'}
          />
          <CustomListItem
            link={`${RouteEnums.Notifications}${
              selectedDealspace?.id ?? workSpaceId ?? id
            }`}
            name={t('navigation.notification')}
            icon={<Icons.Ring />}
            openDrawer={openDrawer}
            count={notificationCount}
            badge={notificationCount}
            id="notificationsNav"
          />
          <CustomListItem
            link={`/milestone/${selectedDealspace?.id ?? workSpaceId ?? id}`}
            name={t('navigation.milestone')}
            icon={<Icons.Flag />}
            openDrawer={openDrawer}
            id="milestonesNav"
          />
        </List>
        <Divider />
        <List>
          <CustomListItem
            link={`/my-tasks/${selectedDealspace?.id ?? workSpaceId ?? id}`}
            name={t('navigation.task')}
            icon={<Icons.AssignedToMe />}
            openDrawer={openDrawer}
            id="myTasksNav"
          />
          <CustomListItem
            link={`/my-risks/${selectedDealspace?.id ?? workSpaceId ?? id}`}
            name={t('navigation.risk')}
            icon={<Icons.Risks20 />}
            openDrawer={openDrawer}
            count={
              Array.from(
                new Set(
                  currentDealTaskList
                    .filter(
                      t =>
                        t.type === 'risk' &&
                        t.projectRelType === 'owner' &&
                        t.isCompleted === false &&
                        !t.isArchived
                    )
                    .map(m => m.id)
                )
              )?.length
            }
            badge={
              !openDrawer
                ? Array.from(
                    new Set(
                      currentDealTaskList
                        .filter(
                          t =>
                            t.type === 'risk' &&
                            t.isCompleted === false &&
                            t.projectRelType === 'owner' &&
                            !t.isArchived
                        )
                        .map(m => m.id)
                    )
                  )?.length
                : 0
            }
          />
          <CustomListItem
            link={`/my-issues/${selectedDealspace?.id ?? workSpaceId ?? id}`}
            name={t('navigation.issue')}
            icon={<Icons.Issues20 />}
            openDrawer={openDrawer}
            count={
              Array.from(
                new Set(
                  currentDealTaskList
                    .filter(
                      t =>
                        t.type === 'issue' &&
                        t.projectRelType === 'owner' &&
                        t.isCompleted === false &&
                        !t.isArchived
                    )
                    .map(m => m.id)
                )
              )?.length
            }
            badge={
              !openDrawer
                ? Array.from(
                    new Set(
                      currentDealTaskList
                        .filter(
                          t =>
                            t.type === 'issue' &&
                            t.projectRelType === 'owner' &&
                            t.isCompleted === false &&
                            !t.isArchived
                        )
                        .map(m => m.id)
                    )
                  )?.length
                : 0
            }
          />
          <CustomListItem
            link={`/my-decisions/${selectedDealspace?.id ?? workSpaceId ?? id}`}
            name={t('navigation.decision')}
            icon={<Icons.Decision20 />}
            openDrawer={openDrawer}
            count={
              currentDealTaskList.filter(
                t =>
                  t.type === 'decision' &&
                  t.isCompleted === false &&
                  t.projectRelType === 'owner' &&
                  !t.isArchived
              )?.length
            }
            badge={
              !openDrawer
                ? currentDealTaskList.filter(
                    t =>
                      t.type === 'decision' &&
                      t.isCompleted === false &&
                      t.projectRelType === 'owner' &&
                      !t.isArchived
                  )?.length
                : 0
            }
          />
          {false ? (
            <CustomListItem
              link={`/dependencies/${
                selectedDealspace?.id ?? workSpaceId ?? id
              }`}
              name={t('navigation.dependency')}
              icon={<Icons.Dependency color="#6E6B8C" />}
              openDrawer={openDrawer}
              count={
                Array.from(
                  new Set(
                    dealTaskList
                      .filter(
                        t =>
                          t.isCompleted === false &&
                          t.type === 'dependency' &&
                          t.playbookFunction === t.dependencyTarget &&
                          !t.isArchived
                      )
                      .map(i => i.id)
                  )
                ).length
              }
              badge={
                !openDrawer
                  ? Array.from(
                      new Set(
                        dealTaskList
                          .filter(
                            t =>
                              t.isCompleted === false &&
                              t.type === 'dependency' &&
                              t.playbookFunction === t.dependencyTarget &&
                              !t.isArchived
                          )
                          .map(i => i.id)
                      )
                    ).length
                  : 0
              }
            />
          ) : (
            <>
              <CustomListItem
                link={`/dependencies-in/${
                  selectedDealspace?.id ?? workSpaceId ?? id
                }`}
                name={t('navigation.dependency_in')}
                icon={<Icons.DependencyIn />}
                openDrawer={openDrawer}
                count={
                  Array.from(
                    new Set(
                      dealTaskList
                        .filter(
                          t =>
                            t.isCompleted === false &&
                            t.type === 'dependency' &&
                            t.dependencyTarget &&
                            t.playbookFunction === t.dependencyTarget &&
                            !t.isArchived
                        )
                        .map(i => i.id)
                    )
                  ).length
                }
                badge={
                  !openDrawer
                    ? Array.from(
                        new Set(
                          dealTaskList
                            .filter(
                              t =>
                                t.isCompleted === false &&
                                t.type === 'dependency' &&
                                t.playbookFunction === t.dependencyTarget &&
                                t.dependencyTarget &&
                                !t.isArchived
                            )
                            .map(i => i.id)
                        )
                      ).length
                    : 0
                }
              />
              <CustomListItem
                link={`/dependencies-out/${
                  selectedDealspace?.id ?? workSpaceId ?? id
                }`}
                name={t('navigation.dependency_out')}
                icon={<Icons.DependencyOut />}
                openDrawer={openDrawer}
                count={
                  Array.from(
                    new Set(
                      dealTaskList
                        .filter(
                          t =>
                            t.isCompleted === false &&
                            t.type === 'dependency' &&
                            t.playbookFunction === t.dependencySource &&
                            t.dependencyTarget &&
                            !t.isArchived
                        )
                        .map(i => i.id)
                    )
                  ).length
                }
                badge={
                  !openDrawer
                    ? Array.from(
                        new Set(
                          dealTaskList
                            .filter(
                              t =>
                                t.isCompleted === false &&
                                t.type === 'dependency' &&
                                t.dependencyTarget &&
                                t.playbookFunction === t.dependencySource &&
                                !t.isArchived
                            )
                            .map(i => i.id)
                        )
                      ).length
                    : 0
                }
              />
            </>
          )}
        </List>
        <Divider />

        {selectedDealspace ? (
          <NavPlaybook
            selectedDealspace={selectedDealspace!}
            refresh={refresh}
            menuShow={openDrawer}
          />
        ) : null}
      </div>
      <div style={{ marginTop: 'auto', padding: 0, margin: 0 }}>
        <Divider />
        <List>
          <CustomListItem
            link={`${RouteEnums.DealspaceDirectory}${
              selectedDealspace?.id ?? workSpaceId ?? id
            }`}
            name={t('navigation.invite')}
            icon={<Icons.People />}
            openDrawer={openDrawer}
            id="dealspaceDirectoryNav"
          />
        </List>
      </div>
    </Drawer>
  );
};

export default Navigation;
